import {
  actionColumn,
  Button,
  CollapseToggle,
  column,
  Css,
  getTableStyles,
  GridColumn,
  GridDataRow,
  GridStyle,
  GridTable,
  Icon,
  IconButton,
  marker,
  Palette,
  ScrollableContent,
  simpleHeader,
  SimpleHeaderAndData,
  SuperDrawerWidth,
  useModal,
  useSuperDrawer,
} from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import { chipCell, emptyCellDash, SearchBox } from "src/components";
import { FeatureTypeDrawer } from "src/routes/admin/featureTypes/FeatureTypeDrawer";
import {
  FeatureTypeDetailsFragment,
  FeatureTypesDocument,
  useDeleteFeatureTypeMutation,
  useFeatureTypesQuery,
} from "src/routes/admin/featureTypes/FeatureTypes.generated";
import { DeleteConfirmationModal } from "src/routes/components/DeleteConfirmationModal";
import { PageHeader } from "src/routes/layout/PageHeader";
import { TableActions } from "src/routes/layout/TableActions";
import { queryResult } from "src/utils";

export function FeatureTypesPage() {
  const query = useFeatureTypesQuery();
  return queryResult(query, ({ featureTypes }) => <FeatureTypesView featureTypes={featureTypes} />);
}

type FeatureTypesViewProps = { featureTypes: FeatureTypeDetailsFragment[] };

type Row = SimpleHeaderAndData<FeatureTypeDetailsFragment>;

function FeatureTypesView(props: FeatureTypesViewProps) {
  const { featureTypes } = props;
  const { openInDrawer } = useSuperDrawer();
  const { openModal } = useModal();
  const [deleteFeatureType] = useDeleteFeatureTypeMutation({
    refetchQueries: [FeatureTypesDocument],
    awaitRefetchQueries: true,
  });

  const openCreateDrawer = useCallback(
    (parent?: FeatureTypeDetailsFragment) => {
      openInDrawer({ content: <FeatureTypeDrawer mode="create" parent={parent} />, width: SuperDrawerWidth.Small });
    },
    [openInDrawer],
  );

  const openEditDrawer = useCallback(
    (featureType: FeatureTypeDetailsFragment) => {
      openInDrawer({
        content: <FeatureTypeDrawer mode="edit" featureType={featureType} />,
        width: SuperDrawerWidth.Small,
      });
    },
    [openInDrawer],
  );

  const openDeleteModal = useCallback(
    (featureType: FeatureTypeDetailsFragment) => {
      openModal({
        content: (
          <DeleteConfirmationModal
            entityType="Feature Type"
            confirmationMessage={
              <span>
                Are you sure you want to delete <strong>{featureType.name}</strong>? This will also delete any nested
                feature types. This action cannot be undone.
              </span>
            }
            onConfirmDelete={async () => {
              await deleteFeatureType({ variables: { input: { id: featureType.id } } });
            }}
          />
        ),
      });
    },
    [deleteFeatureType, openModal],
  );

  const rows = useMemo(() => createRows(featureTypes), [featureTypes]);
  const columns = useMemo(() => createColumns(openCreateDrawer, openDeleteModal), [openCreateDrawer, openDeleteModal]);
  const [search, setSearch] = useState<string | undefined>("");

  return (
    <>
      <PageHeader title="Feature Types" />
      <TableActions>
        <SearchBox onSearch={setSearch} placeholder="Search feature types" />
        <Button icon="plus" label="Add Root Feature Type" onClick={() => openCreateDrawer()} />
      </TableActions>
      <ScrollableContent>
        <GridTable<Row>
          rows={rows}
          columns={columns}
          filter={search}
          style={featureTypeTableStyle}
          rowStyles={{ data: { onClick: ({ data }) => openEditDrawer(data), rowCss: Css.cursorPointer.marker.$ } }}
        />
      </ScrollableContent>
    </>
  );
}

function createRows(featureTypes: FeatureTypeDetailsFragment[]): GridDataRow<Row>[] {
  const featureTypesByParent = featureTypes.groupBy((ft) => ft.parent?.id ?? "");
  function build(ft: FeatureTypeDetailsFragment): GridDataRow<Row> {
    const childRows = (featureTypesByParent[ft.id] ?? []).map(build);
    return {
      kind: "data" as const,
      id: ft.id,
      // Spread `ft` so the data reference changes on every rebuild. Beam's RowState only observes
      // the data ref (not row.children), so without this, parents that gain their first child
      // don't re-render and the CollapseToggle stays hidden until the page is refreshed.
      data: { ...ft },
      ...(childRows.nonEmpty ? { children: childRows } : {}),
    };
  }
  const roots = (featureTypesByParent[""] ?? []).map(build);
  return [simpleHeader, ...roots];
}

function createColumns(
  openCreateDrawer: (parent?: FeatureTypeDetailsFragment) => void,
  openDeleteModal: (featureType: FeatureTypeDetailsFragment) => void,
): GridColumn<Row>[] {
  return [
    column<Row>({
      header: "Name",
      data: (ft, { row }) => ({
        content: (
          <div css={Css.df.aic.gap1.fg1.$}>
            <div css={Css.wPx(20).df.aic.jcc.$}>
              {row.children?.nonEmpty ? <CollapseToggle row={row} compact /> : undefined}
            </div>
            <span>{ft.name}</span>
          </div>
        ),
        value: ft.name,
      }),
      mw: "240px",
    }),
    column<Row>({
      header: "Allowed In Building",
      data: (ft) =>
        ft.allowedInBuilding ? { content: <Icon icon="check" color={Palette.Gray700} />, value: "" } : emptyCellDash,
      w: "180px",
    }),
    column<Row>({
      header: "Allowed In Level",
      data: (ft) =>
        ft.allowedInLevel ? { content: <Icon icon="check" color={Palette.Gray700} />, value: "" } : emptyCellDash,
      w: "180px",
    }),
    column<Row>({
      header: "Room Types",
      data: (ft) => {
        const names = ft.roomTypes.map((rt) => rt.name).toSorted();
        if (!names.nonEmpty) return { content: emptyCellDash, value: "" };
        return { ...chipCell(names), value: "" };
      },
      mw: "240px",
    }),
    actionColumn<Row>({
      header: "",
      data: (ft) => ({
        content: () => (
          <div css={Css.df.aic.gap1.o0.when(marker, "ancestor", ":hover").o100.$}>
            <Button variant="tertiary" icon="plus" label="Add Child" onClick={() => openCreateDrawer(ft)} />
            <IconButton icon="trash" onClick={() => openDeleteModal(ft)} />
          </div>
        ),
      }),
      w: "180px",
    }),
  ];
}

const featureTypeTableStyle: GridStyle = {
  ...getTableStyles({ allWhite: true, bordered: true }),
  levels: (level) => ({ rowIndent: level > 0 ? 24 * level : undefined }),
};
